// src/config/paymentMethods.js
/**
 * 💳 CATALOGUE DES MOYENS DE PAIEMENT (source unique)
 * ==================================================
 * Utilisé par le modal de paiement, le gestionnaire de moyens enregistrés et le portefeuille.
 *
 * Activer un moyen de paiement = une variable d'environnement (voir .env.example), sans toucher au code :
 *   - Mobile Money (Nita / Amana) : REACT_APP_ENABLE_MOBILE_MONEY=true, BIA CASH : REACT_APP_ENABLE_BIA_CASH=true
 *     (backend /api/nita, /api/amana, /api/bia-cash). Désactivés par défaut : sans compte marchand, le code
 *     USSD affiché n'encaisserait rien et le paiement resterait « en attente ».
 *   - Virement bancaire : REACT_APP_ENABLE_BANK_TRANSFER (actif par défaut), confirmé par un administrateur
 *   - Carte bancaire   : REACT_APP_ENABLE_CARD_PAYMENTS=true + clés Stripe côté backend (Stripe Checkout)
 *   - PayPal           : REACT_APP_ENABLE_PAYPAL=true + clés PayPal côté backend (Orders API v2)
 *   - Cryptomonnaie    : non proposée (non reconnue par la BCEAO dans l'UEMOA)
 */

// Résumé du paiement en cours, conservé le temps de la redirection vers Stripe / PayPal
/** Montant minimum d'un investissement, en FCFA (même règle que le serveur de paiement) */
export const MIN_INVESTMENT = 100000;

/** Montant minimum affiché dans les textes : « 100 000 FCFA » */
export const MIN_INVESTMENT_LABEL = `${MIN_INVESTMENT.toLocaleString('fr-FR')} FCFA`;

export const PENDING_CHECKOUT_KEY = 'pending_checkout';

const flag = (value, defaultValue) => (value === undefined || value === '' ? defaultValue : value === 'true');

export const MOBILE_OPERATORS = {
  nita: {
    label: 'Nita Transfert (Airtel)',
    operatorName: 'Airtel',
    prefixes: ['99', '98', '97'],
    methodCode: 'nita_transfert'
  },
  amana: {
    label: 'Amana Money (Orange)',
    operatorName: 'Orange',
    prefixes: ['96', '95', '94'],
    methodCode: 'amana_money'
  }
};

export const BANK_DETAILS = {
  bankName: process.env.REACT_APP_BANK_NAME || 'BIA Niger',
  accountHolder: process.env.REACT_APP_BANK_ACCOUNT_HOLDER || 'Community ImpactInvest',
  accountNumber: process.env.REACT_APP_BANK_ACCOUNT_NUMBER || '025311169805/18',
  iban: process.env.REACT_APP_BANK_IBAN || '',
  swift: process.env.REACT_APP_BANK_SWIFT || ''
};

/**
 * id : identifiant dans l'interface · methodCode : valeur enregistrée dans investments.payment_method
 * flow : 'mobile_money' | 'bia_cash' | 'bank_transfer' | 'redirect' (paiement sur la page du prestataire)
 */
export const PAYMENT_METHODS = [
  {
    id: 'mobile-money-niger',
    name: 'Mobile Money Niger',
    description: 'Nita Transfert (Airtel) / Amana Money (Orange)',
    flow: 'mobile_money',
    fees: 'frais opérateur',
    processingTime: 'Instantané',
    enabled: flag(process.env.REACT_APP_ENABLE_MOBILE_MONEY, false)
  },
  {
    id: 'bia-cash',
    name: 'BIA CASH',
    description: 'Transfert depuis votre compte BIA CASH',
    flow: 'bia_cash',
    methodCode: 'bia_cash',
    fees: 'selon votre banque',
    processingTime: '5 à 10 minutes',
    enabled: flag(process.env.REACT_APP_ENABLE_BIA_CASH, false)
  },
  {
    id: 'bank-transfer',
    name: 'Virement bancaire',
    description: 'Virement vers le compte de la plateforme, avec votre référence',
    flow: 'bank_transfer',
    methodCode: 'bank_transfer',
    fees: 'selon votre banque',
    processingTime: '1 à 3 jours ouvrés',
    enabled: flag(process.env.REACT_APP_ENABLE_BANK_TRANSFER, true)
  },
  {
    id: 'credit-card',
    name: 'Carte bancaire',
    description: 'Visa, Mastercard — page de paiement sécurisée Stripe',
    flow: 'redirect',
    provider: 'stripe',
    methodCode: 'stripe',
    fees: 'incluses',
    processingTime: 'Instantané',
    enabled: flag(process.env.REACT_APP_ENABLE_CARD_PAYMENTS, false)
  },
  {
    id: 'paypal',
    name: 'PayPal',
    description: 'Validation sur le site de PayPal (débit en euros)',
    flow: 'redirect',
    provider: 'paypal',
    methodCode: 'paypal',
    fees: 'incluses',
    processingTime: 'Instantané',
    enabled: flag(process.env.REACT_APP_ENABLE_PAYPAL, false)
  },
  {
    id: 'crypto',
    name: 'Cryptomonnaie',
    description: 'Non proposée : non reconnue comme moyen de paiement dans l\'UEMOA',
    flow: 'none',
    enabled: false
  }
];

/**
 * Moyens de paiement ouverts, en toutes lettres, pour les textes du site :
 * « Nita Transfert (Airtel), Amana Money (Orange), BIA CASH ou virement bancaire ».
 * La carte (Visa, Mastercard) et PayPal n'y figurent que lorsqu'ils sont activés.
 */
export const paymentMethodsText = (methods = PAYMENT_METHODS) => {
  const names = [];
  methods.filter(method => method.enabled).forEach(method => {
    if (method.flow === 'mobile_money') names.push(...Object.values(MOBILE_OPERATORS).map(operator => operator.label));
    else if (method.id === 'credit-card') names.push('carte bancaire Visa ou Mastercard');
    else if (method.id === 'bank-transfer') names.push('virement bancaire');
    else names.push(method.name);
  });
  if (names.length < 2) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} ou ${names[names.length - 1]}`;
};

/** Phrase calculée une fois au chargement (les activations se font au déploiement) */
export const PAYMENT_METHODS_TEXT = paymentMethodsText();

export const PAYMENT_METHOD_LABELS = {
  nita_transfert: 'Nita Transfert (Airtel)',
  amana_money: 'Amana Money (Orange)',
  bia_cash: 'BIA CASH',
  bank_transfer: 'Virement bancaire',
  stripe: 'Carte bancaire',
  paypal: 'PayPal'
};

const NIGER_PHONE_REGEX = /^(\+227|227|0)?9[0-9]{7}$/;

export const cleanPhoneNumber = (phoneNumber) => String(phoneNumber || '').replace(/[\s\-().]/g, '');

/** Opérateur déduit du préfixe du numéro (+227 9X…), ou null */
export const detectOperator = (phoneNumber) => {
  const prefix = cleanPhoneNumber(phoneNumber).replace(/\D/g, '').slice(-8, -6);
  return Object.keys(MOBILE_OPERATORS).find(key => MOBILE_OPERATORS[key].prefixes.includes(prefix)) || null;
};

/** @returns {string|null} message d'erreur, ou null si le numéro convient à l'opérateur */
export const validateMobileNumber = (operator, phoneNumber) => {
  const clean = cleanPhoneNumber(phoneNumber);
  if (!clean || clean === '+227') return 'Numéro de téléphone requis';
  if (!NIGER_PHONE_REGEX.test(clean)) return 'Numéro invalide. Format attendu : +227 9X XX XX XX';
  const config = MOBILE_OPERATORS[operator];
  if (!config) return 'Choisissez votre opérateur';
  if (detectOperator(clean) !== operator) {
    return `Ce numéro n'est pas un numéro ${config.operatorName} (préfixes ${config.prefixes.join(', ')})`;
  }
  return null;
};

export const validateBiaAccount = (account) =>
  (/^[A-Za-z0-9-]{4,34}$/.test(String(account || '').trim()) ? null : 'Numéro de compte BIA CASH invalide');

/** "+227 99 60 26 00" → "+227 •• •• 26 00" */
export const maskPhoneNumber = (phoneNumber) => {
  const digits = String(phoneNumber).replace(/\D/g, '');
  return `+227 •• •• ${digits.slice(-4, -2)} ${digits.slice(-2)}`;
};

/** Référence unique à indiquer dans le libellé d'un virement, ex. "II-7K3Q9P2M" */
export const generatePaymentReference = () => {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(8);
  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    bytes.forEach((_, i) => { bytes[i] = Math.floor(Math.random() * 256); });
  }
  return `II-${Array.from(bytes, b => alphabet[b % alphabet.length]).join('')}`;
};
